import { useFieldContext } from '@ark-ui/solid/field';
import { IconChevronDown } from '@bbr/icons/chevron-down-outline';
import { useContext, mergeProps, splitProps, Show } from 'solid-js';
import { Field, InputContext } from './field.tsx';
import { FormContext, disableAutocomplete } from './form.tsx';
import type { ComponentProps, JSX, JSXElement } from 'solid-js';

type Props = Omit<JSX.SelectHTMLAttributes<HTMLSelectElement>, 'aria-required'>;

export function Select(props: ComponentProps<typeof Field>) {
	return <Field {...props} />;
}

Select.Label = (props: ComponentProps<typeof Field.Label>) => <Field.Label {...props} />;

Select.Control = (inputProps: Props) => {
	const { data } = useContext(FormContext);
	const inputContext = useContext(InputContext);
	const merged = mergeProps(inputContext[0], inputProps);
	const [, props] = splitProps(merged, ['children']);
	const field = useFieldContext();

	return (
		<div class="group relative flex items-center">
			<select
				tabindex={0}
				{...field().getSelectProps()}
				{...props}
				disabled={data().pending || props.disabled}
				aria-required={props.required}
				{...disableAutocomplete(props)}
				class="w-full appearance-none rounded-md border border-zinc-300 bg-white px-3 py-2 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 focus-visible:ring-4 focus-visible:ring-yellow-500/50 focus-visible:ring-offset-1 focus-visible:ring-offset-zinc-50 disabled:bg-zinc-100 disabled:text-zinc-500 aria-invalid:border-rose-600 aria-readonly:cursor-not-allowed aria-readonly:bg-zinc-100 aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:focus-visible:ring-offset-zinc-950 dark:disabled:bg-zinc-800 dark:aria-readonly:bg-zinc-900"
			>
				{inputProps.children}
			</select>
			<IconChevronDown class="pointer-events-none absolute right-2 size-6 text-zinc-500" />
		</div>
	);
};

type OptionProps = JSX.OptionHTMLAttributes<HTMLOptionElement>;

Select.Group = (props: JSX.OptgroupHTMLAttributes<HTMLOptGroupElement>) => <optgroup {...props} />;

Select.Item = (props: OptionProps) => {
	return <option {...props} />;
};

export function SelectField(
	props: ComponentProps<typeof Select.Control> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Field name={rest.name ?? ''}>
			<Select.Label>{other.label}</Select.Label>
			<Select.Control {...rest} />
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Field>
	);
}
